/**
 * Chart capability registry — single source of truth for per-chart structural
 * and feature metadata used across axisNodes, axisGuideLines, axisDisplayFormat,
 * LayoutStylePanel, AxisStylePanel, and ChartOptions.
 *
 * Axis SVG patterns (axis.pattern):
 *   'direct'    — #xAxis / #yAxis element IDs
 *   'container' — #axis group; x/y children detected by y-transform magnitude
 *   'spaced'    — "[id='x axis']" / "[id='y axis']" attribute selectors
 *   'none'      — no cartesian axes rendered
 */

export const CHART_CAPABILITIES = {

  // ── Network / Flow ────────────────────────────────────────────────────────

  'rawgraphs.forcedirectedgraph': {
    name: 'Force-directed graph',
    axis: { pattern: 'none', hasX: false, hasY: false },
    colorScale: { dimension: 'group' },
    optionGroups: ['artboard', 'layout', 'nodes', 'links', 'labels', 'colors'],
    features: {
      series: false, repeatForLabel: false,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: false,
    },
    quirks: ['one source-target row represents one relationship; repeated identical links increase line width'],
  },

  'rawgraphs.alluvialdiagram': {
    name: 'Alluvial Diagram',
    axis: { pattern: 'none', hasX: false, hasY: false },
    colorScale: { dimension: 'source' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels'],
    features: {
      series: false, repeatForLabel: false,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: false,
    },
    quirks: ['linksBlendMode — color-burn option patched in by ChartOptions'],
  },

  'rawgraphs.arcdiagram': {
    name: 'Arc Diagram',
    axis: { pattern: 'none', hasX: false, hasY: false },
    colorScale: { dimension: 'source' },
    optionGroups: ['artboard', 'chart', 'colors'],
    features: {
      series: false, repeatForLabel: false,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: false,
    },
    quirks: [],
  },

  'rawgraphs.sankeydiagram': {
    name: 'Sankey Diagram',
    axis: { pattern: 'none', hasX: false, hasY: false },
    colorScale: { dimension: 'source' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels'],
    features: {
      series: false, repeatForLabel: false,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: false,
    },
    quirks: [
      'linksBlendMode — color-burn option patched in by ChartOptions',
      'iterations — high values cause layout thrashing; Sankey-specific setting',
    ],
  },

  'rawgraphs.chorddiagram': {
    name: 'Chord Diagram',
    axis: { pattern: 'none', hasX: false, hasY: false },
    colorScale: { dimension: null },
    optionGroups: ['artboard', 'chart', 'colors', 'labels'],
    features: {
      series: false, repeatForLabel: false,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: [],
  },

  // ── Hierarchy ─────────────────────────────────────────────────────────────

  'rawgraphs.circlepacking': {
    name: 'Circle Packing',
    axis: { pattern: 'none', hasX: false, hasY: false },
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels'],
    features: {
      series: false, repeatForLabel: true,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: [],
  },

  'rawgraphs.circulardendrogram': {
    name: 'Circular Dendrogram',
    axis: { pattern: 'none', hasX: false, hasY: false },
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels'],
    features: {
      series: false, repeatForLabel: true,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: [],
  },

  'rawgraphs.lineardendrogram': {
    name: 'Linear Dendrogram',
    axis: { pattern: 'container', hasX: true, hasY: false }, // verify: agent found #axis
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels'],
    features: {
      series: false, repeatForLabel: true,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: false,
    },
    quirks: [],
  },

  'rawgraphs.sunburst': {
    name: 'Sunburst',
    axis: { pattern: 'none', hasX: false, hasY: false },
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels'],
    features: {
      series: false, repeatForLabel: true,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: [],
  },

  'rawgraphs.treemap': {
    name: 'Treemap',
    axis: { pattern: 'spaced', hasX: true, hasY: true },
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels'],
    features: {
      series: false, repeatForLabel: true,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: [],
  },

  'rawgraphs.voronoitreemap': {
    name: 'Voronoi Treemap',
    axis: { pattern: 'none', hasX: false, hasY: false },
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels'],
    features: {
      series: false, repeatForLabel: true,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: ['iterations — voronoi relaxation passes; high values are slow'],
  },

  // ── Bar Charts ────────────────────────────────────────────────────────────

  'rawgraphs.barchart': {
    name: 'Bar Chart',
    axis: { pattern: 'direct', hasX: true, hasY: true },
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors', 'series'],
    features: {
      series: true, repeatForLabel: false,
      showGrid: true, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: [],
  },

  'rawgraphs.barchartmultiset': {
    name: 'Multi-set Bar Chart',
    axis: { pattern: 'direct', hasX: true, hasY: true },
    colorScale: { dimension: 'bars' },
    optionGroups: ['artboard', 'chart', 'colors', 'series'],
    features: {
      series: true, repeatForLabel: false,
      showGrid: true, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: [],
  },

  'rawgraphs.barchartstacked': {
    name: 'Stacked Bar Chart',
    axis: { pattern: 'direct', hasX: true, hasY: true },
    colorScale: { dimension: 'bars' },
    optionGroups: ['artboard', 'chart', 'colors', 'series'],
    features: {
      series: true, repeatForLabel: false,
      showGrid: true, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: [],
  },

  // ── Line / Time Series ────────────────────────────────────────────────────

  'rawgraphs.linechart': {
    name: 'Line Chart',
    axis: { pattern: 'container', hasX: true, hasY: true },
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'axes', 'chart', 'colors', 'labels', 'series'],
    features: {
      series: true, repeatForLabel: false,
      showGrid: true, showSeriesGrid: true, lineStrokeWidth: true, showLegend: true,
    },
    quirks: ['only chart with an "axes" option group in the standard UI'],
  },

  'rawgraphs.bumpchart': {
    name: 'Bumpchart',
    axis: { pattern: 'direct', hasX: true, hasY: true }, // verify: disputed between research passes
    colorScale: { dimension: 'streams' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels', 'series'],
    features: {
      series: true, repeatForLabel: false,
      showGrid: true, showSeriesGrid: false, lineStrokeWidth: true, showLegend: true,
    },
    quirks: ['sortSeriesBy — only enabled when series dimension is mapped (ChartOptions special case)'],
  },

  'rawgraphs.slopechart': {
    name: 'Slope Chart',
    axis: { pattern: 'direct', hasX: false, hasY: true }, // #yAxis only
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors', 'series'],
    features: {
      series: true, repeatForLabel: false,
      showGrid: true, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: [],
  },

  'rawgraphs.streamgraph': {
    name: 'Streamgraph',
    axis: { pattern: 'direct', hasX: true, hasY: false }, // #xAxis only
    colorScale: { dimension: 'streams' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels', 'series'],
    features: {
      series: true, repeatForLabel: false,
      showGrid: true, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: [],
  },

  'rawgraphs.horizongraph': {
    name: 'Horizon Graph',
    axis: { pattern: 'container', hasX: true, hasY: true },
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors'],
    features: {
      series: false, repeatForLabel: false,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: false,
    },
    quirks: ['axis children appended y-first then x — handled by transform-based detection in axisNodes'],
  },

  // ── Scatter / Distribution ────────────────────────────────────────────────

  'rawgraphs.bubblechart': {
    name: 'Bubble Chart',
    axis: { pattern: 'container', hasX: true, hasY: true },
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels', 'series'],
    features: {
      series: true, repeatForLabel: true,
      showGrid: true, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: [],
  },

  'rawgraphs.beeswarm': {
    name: 'Beeswarm Plot',
    axis: { pattern: 'container', hasX: true, hasY: true },
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels', 'series'],
    features: {
      series: true, repeatForLabel: true,
      showGrid: true, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: [],
  },

  'rawgraphs.contourplot': {
    name: 'Contour Plot',
    axis: { pattern: 'container', hasX: true, hasY: true },
    colorScale: { dimension: null },
    optionGroups: ['artboard', 'chart', 'colors', 'labels'],
    features: {
      series: false, repeatForLabel: false,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: [],
  },

  'rawgraphs.hexagonalbinning': {
    name: 'Hexagonal Binning',
    axis: { pattern: 'container', hasX: true, hasY: true },
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels'],
    features: {
      series: false, repeatForLabel: false,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: [],
  },

  'rawgraphs.convexhull': {
    name: 'Convex Hull',
    axis: { pattern: 'container', hasX: true, hasY: true },
    colorScale: { dimension: 'group' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels'],
    features: {
      series: false, repeatForLabel: true,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: false,
    },
    quirks: [],
  },

  'rawgraphs.voronoidiagram': {
    name: 'Voronoi Diagram',
    axis: { pattern: 'container', hasX: true, hasY: true },
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels'],
    features: {
      series: false, repeatForLabel: false,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: [],
  },

  'rawgraphs.matrixplot': {
    name: 'Matrix Plot',
    axis: { pattern: 'none', hasX: false, hasY: false },
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels'],
    features: {
      series: false, repeatForLabel: true,
      showGrid: true, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: [],
  },

  // ── Statistical ───────────────────────────────────────────────────────────

  'rawgraphs.boxplot': {
    name: 'Box Plot',
    axis: { pattern: 'spaced', hasX: true, hasY: true },
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels'],
    features: {
      series: false, repeatForLabel: false,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: false,
    },
    quirks: [],
  },

  'rawgraphs.violinplot': {
    name: 'Violin Plot',
    axis: { pattern: 'spaced', hasX: true, hasY: true },
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors'],
    features: {
      series: false, repeatForLabel: false,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: true, showLegend: false,
    },
    quirks: [],
  },

  // ── Radial ────────────────────────────────────────────────────────────────

  'rawgraphs.radarchart': {
    name: 'Radar Chart',
    axis: { pattern: 'spaced', hasX: false, hasY: true }, // #y axis only (radial)
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors', 'labels', 'series'],
    features: {
      series: true, repeatForLabel: false,
      showGrid: true, showSeriesGrid: false, lineStrokeWidth: false, showLegend: true,
    },
    quirks: [],
  },

  'rawgraphs.piechart': {
    name: 'Pie / Donut Chart',
    axis: { pattern: 'none', hasX: false, hasY: false },
    colorScale: { dimension: null },
    optionGroups: ['artboard', 'chart', 'colors', 'labels', 'series'],
    features: {
      series: true, repeatForLabel: false,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: false, showLegend: false,
    },
    quirks: [],
  },

  // ── Multi-dimensional ─────────────────────────────────────────────────────

  'rawgraphs.parallelcoordinates': {
    name: 'Parallel Coordinates',
    axis: { pattern: 'none', hasX: false, hasY: false },
    colorScale: { dimension: 'color' },
    optionGroups: ['artboard', 'chart', 'colors'],
    features: {
      series: false, repeatForLabel: false,
      showGrid: false, showSeriesGrid: false, lineStrokeWidth: true, showLegend: true,
    },
    quirks: [],
  },
}

// ── Derived helpers ──────────────────────────────────────────────────────────

/**
 * Returns the capabilities entry for a chart ID, or null if unknown.
 */
export function getChartCapabilities(chartId) {
  return CHART_CAPABILITIES[chartId] ?? null
}

/**
 * True when the chart renders cartesian axis SVG elements that axis guide
 * lines and display-format services can operate on.
 */
export function chartHasCartesianAxes(chartId) {
  const cap = getChartCapabilities(chartId)
  if (!cap) return false
  return cap.axis.hasX || cap.axis.hasY
}

/**
 * True when the chart supports the showSeriesGrid panel option.
 */
export function chartSupportsSeriesGrid(chartId) {
  return getChartCapabilities(chartId)?.features?.showSeriesGrid ?? false
}

/**
 * True when the chart supports a line/stroke width option.
 */
export function chartSupportsLineStrokeWidth(chartId) {
  return getChartCapabilities(chartId)?.features?.lineStrokeWidth ?? false
}
